import { sectionStatusHandler, text } from "@theme/fetch";
import { morph, parseHtml } from "@theme/helpers";
import { HTMLCustomElement } from "@theme/html-custom-element";

/**
 * Handles filter and sort selections with the Section Rendering API.
 */
class ResultsList extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on("change", this.refs.sort, this.handleFormSubmit.bind(this));
    this.on("change", this.refs.filters, this.handleFormSubmit.bind(this));
    this.on("click", this.refs.close, this.handleClose.bind(this));
    this.on("click", this, this.handleLoadMoreClick.bind(this));
    this.observeLoadMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.loadMoreObserver?.disconnect();
  }

  /**
   * Handles the sort or filter form submit event.
   * @param {SubmitEvent} event - The form submit event.
   */
  async handleFormSubmit(event) {
    event.preventDefault();

    const data = [
      ...new FormData(this.refs.filters),
      ...new FormData(this.refs.sort),
    ];
    const params = this.filterSearchParams(new URLSearchParams(data));
    const section = this.sectionId
      ? ["section_id", this.sectionId]
      : [null, null];
    const query = new URLSearchParams([
      ...params.entries(),
      section,
    ]).toString();
    const { origin, pathname } = window.location;

    /**
     * Update the query parameters.
     */
    window.history.pushState(
      {},
      "",
      `${origin}${pathname}${params.size === 0 ? "" : `?${params.toString()}`}`,
    );

    try {
      await text(
        fetch(`${origin}${pathname}?${query}`),
        sectionStatusHandler,
      ).then(async (response) => {
        await morph(this, parseHtml(response, this.tagName.toLowerCase()));

        /**
         * Re-observe the load more element, since the previous grid
         * (and its button) was replaced by the filter/sort re-render.
         */
        this.observeLoadMore();
      });
    } catch {
      /**
       * Since we couldn't update the results, reload the page.
       */
      window.location.reload();
    }
  }

  /**
   * Observes the load more element and auto-triggers when it enters the viewport.
   */
  observeLoadMore() {
    const loadMoreEl = this.querySelector(".collection-load-more");

    if (!loadMoreEl) return;

    this.loadMoreObserver?.disconnect();

    this.loadMoreObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        this.loadMoreObserver.disconnect();
        this.triggerLoadMore(loadMoreEl.querySelector(".collection-load-more__button"));
      },
      { rootMargin: "0px 0px 300px 0px" },
    );

    this.loadMoreObserver.observe(loadMoreEl);
  }

  /**
   * Handles a manual click on the load more button.
   * @param {MouseEvent} event
   */
  handleLoadMoreClick(event) {
    const button = event.target.closest(".collection-load-more__button");

    if (!button) return;

    event.preventDefault();
    this.triggerLoadMore(button);
  }

  /**
   * Fetches the next page and appends its product items to the grid.
   * @param {HTMLButtonElement} button
   */
  async triggerLoadMore(button) {
    if (!button || button.disabled) return;

    const nextUrl = button.dataset.nextUrl;

    if (!nextUrl) return;

    button.disabled = true;

    const url = new URL(nextUrl, window.location.origin);

    if (this.sectionId) {
      url.searchParams.set("section_id", this.sectionId);
    }

    try {
      const response = await text(
        fetch(url.toString()),
        sectionStatusHandler,
      );

      const parsed = parseHtml(response, "results-list");
      const newItems = parsed.querySelectorAll(".collection-products-grid > li");
      const grid = this.refs.grid;

      newItems.forEach((item) => grid.appendChild(item));

      const newLoadMore = parsed.querySelector(".collection-load-more");
      const currentLoadMore = this.querySelector(".collection-load-more");

      if (newLoadMore) {
        currentLoadMore.replaceWith(newLoadMore);
      } else if (currentLoadMore) {
        currentLoadMore.remove();
      }

      this.observeLoadMore();
    } catch {
      button.disabled = false;
      this.observeLoadMore();
    }
  }

  /**
   * Closes the filters details element.
   */
  handleClose() {
    this.refs.details.removeAttribute("open");
  }

  /**
   * Filters out empty values, or the default sort by option.
   * @param {URLSearchParams} params - The search params.
   * @returns {URLSearchParams}
   */
  filterSearchParams(params) {
    return new URLSearchParams([
      ...params.entries().filter(([key, value]) => {
        if (value === "") {
          return false;
        }

        if (key === "sort_by" && this.hasAttribute("default-sort-by")) {
          return !(value === this.getAttribute("default-sort-by"));
        }

        return true;
      }),
    ]);
  }

  /**
   * The section container.
   */
  get sectionId() {
    return this.getAttribute("section-id");
  }
}

if (!customElements.get("results-list"))
  customElements.define("results-list", ResultsList);
